import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type OnChangeFn = (_: unknown) => void;
type OnTouchedFn = () => void;

/**
 * @todo Extract to shared component
 */
@Component({
  selector: 'app-radio',
  templateUrl: 'radio.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input()
  public internalId: string;

  @Input()
  public internalControlName: string;

  @Input()
  public internalControl: FormControl;

  @Input()
  public iconPath: string;

  @Input()
  public iconSize: 1|2|3 = 3;

  @Input()
  public title: string;

  @Input()
  public description: string;

  @Input()
  public value: unknown;

  @Input()
  public isDisabled?: boolean = false;

  public touched = false;

  private readonly destroyed$: Subject<void> = new Subject();

  private model: unknown;

  constructor(
    @Self()
    private readonly ngControl: NgControl,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    ngControl.valueAccessor = this;
  }

  public get isChecked(): boolean {
    return this.model === this.value;
  }

  public get iconSizeClass(): string {
    return `svg-icon-${this.iconSize}x`;
  }

  public ngOnInit() {
    this.ngControl.control?.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((value: unknown) => {
        this.writeValue(value);
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onChange: OnChangeFn = (_value: unknown) => {};
  public onTouched: OnTouchedFn = () => {};

  public onClickRadio(): void {
    this.markAsTouched();
    if (this.model !== this.value) {
      this.onChange(this.value);
    }
  }

  public registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  public writeValue(value: unknown): void {
    this.model = value;
  }

  private markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}
